// The chat input is a contenteditable, not an <input>, so a picked emote can
// show as its ARTWORK where you typed it instead of as the bare token. An
// <input> can only ever hold a string, which is why "insert the emote" used to
// mean "insert the letters KEKW".
//
// Everything above this module still works in plain text: what the field HOLDS
// is a string with emote tokens in it, exactly as before, and that is what is
// sent, length-capped and tab-completed. The images are a rendering of that
// string, and this module is the only place that knows the difference.
import { chatInputEl } from './dom.js';
import {
  CHAT_TEXT_LIMIT,
  EMOTE_ATTR,
  nodeText,
  normalizeNodes,
  serializeNodes
} from '../../core/chat-text.js';

// --- the DOM part --------------------------------------------------------

function childNodes() {
  return chatInputEl ? Array.from(chatInputEl.childNodes) : [];
}

function chatInputText() {
  return serializeNodes(childNodes());
}

// Length is counted in the TEXT the field stands for, so an emote costs what
// its token costs -- the same budget the server enforces.
function chatInputLength() {
  return chatInputText().length;
}

function createEmoteNode(name, imageFactory) {
  const img = imageFactory(name);
  img.setAttribute(EMOTE_ATTR, name);
  // Not editable as a unit: without this a caret can land inside the image and
  // backspace deletes "half" of it in some browsers.
  img.setAttribute('contenteditable', 'false');
  img.alt = name;
  img.classList.add('chat-emote-inline');
  return img;
}

function clearChatInput() {
  if (!chatInputEl) return;
  chatInputEl.replaceChildren();
}

function setChatInputText(text) {
  if (!chatInputEl) return;
  chatInputEl.replaceChildren(document.createTextNode(String(text ?? '')));
}

function setChatInputEnabled(enabled) {
  if (!chatInputEl) return;
  const on = Boolean(enabled);
  // contenteditable has no `disabled`; the attribute plus the class is what
  // stops typing and greys it, and aria-disabled is what says so out loud.
  chatInputEl.setAttribute('contenteditable', on ? 'true' : 'false');
  chatInputEl.classList.toggle('is-disabled', !on);
  chatInputEl.setAttribute('aria-disabled', on ? 'false' : 'true');
  if (!on) chatInputEl.blur();
}

function isChatInputEnabled() {
  return Boolean(chatInputEl) && chatInputEl.getAttribute('contenteditable') !== 'false';
}

// Caret position as an offset into the TEXT the field stands for, so callers
// (tab completion) can keep working in string space.
function caretTextOffset() {
  if (!chatInputEl) return 0;
  const selection = window.getSelection?.();
  if (!selection || selection.rangeCount === 0) return chatInputLength();
  const range = selection.getRangeAt(0);
  if (!chatInputEl.contains(range.endContainer)) return chatInputLength();
  let offset = 0;
  for (const node of childNodes()) {
    if (node === range.endContainer) return offset + range.endOffset;
    if (node.contains?.(range.endContainer)) return offset + nodeText(node).length;
    offset += nodeText(node).length;
    // A range can also end BETWEEN children, addressed on the field itself.
    if (range.endContainer === chatInputEl && childNodes().indexOf(node) === range.endOffset - 1) {
      return offset;
    }
  }
  return offset;
}

function selectionIsCollapsed() {
  const selection = window.getSelection?.();
  if (!selection || selection.rangeCount === 0) return true;
  return selection.getRangeAt(0).collapsed;
}

function setCaretAtTextOffset(target) {
  if (!chatInputEl) return;
  const selection = window.getSelection?.();
  if (!selection) return;
  const range = document.createRange();
  let remaining = Math.max(0, Number(target) || 0);
  for (const node of childNodes()) {
    const length = nodeText(node).length;
    if (node.nodeType === 3 && remaining <= length) {
      range.setStart(node, remaining);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
      return;
    }
    // An emote is atomic: a caret inside it is a caret after it.
    if (remaining <= length && node.nodeType !== 3) {
      range.setStartAfter(node);
      range.collapse(true);
      selection.removeAllRanges();
      selection.addRange(range);
      return;
    }
    remaining -= length;
  }
  range.selectNodeContents(chatInputEl);
  range.collapse(false);
  selection.removeAllRanges();
  selection.addRange(range);
}

// Strips anything that is not text or an emote, and enforces the length cap.
// Runs after every input, so pasted markup and formatting shortcuts cannot
// build up state this module's serializer would have to understand.
function normalizeChatInput() {
  if (!chatInputEl) return;
  for (const { node, action, text } of normalizeNodes(childNodes())) {
    if (action === 'keep') continue;
    node.replaceWith(document.createTextNode(text));
  }
  chatInputEl.normalize();
  const text = chatInputText();
  if (text.length > CHAT_TEXT_LIMIT) {
    setChatInputText(text.slice(0, CHAT_TEXT_LIMIT));
    setCaretAtTextOffset(CHAT_TEXT_LIMIT);
  }
}

export {
  caretTextOffset,
  chatInputLength,
  chatInputText,
  clearChatInput,
  createEmoteNode,
  isChatInputEnabled,
  normalizeChatInput,
  selectionIsCollapsed,
  setCaretAtTextOffset,
  setChatInputEnabled,
  setChatInputText
};
